// Theme-aware sila brand mark.
//
// Behaviour:
//   • Light mode → native colours (grey letters + orange dot).
//   • Dark mode  → tries `sila-logo-dark.webp` first. If that file
//     isn't on disk (404 on load), falls back to `sila-logo.webp`
//     with a `brightness(0) invert(1)` filter so the mark stays
//     legible on dark backgrounds without vanishing.
//
// `variant="brand-panel"` forces the white/inverted treatment
// regardless of theme — used on the login-screen brand panel which
// is always the dark charcoal gradient.

import { useState } from 'react'
import { Box, type SxProps, type Theme } from '@mui/material'
import { APP_LOGO, APP_LOGO_DARK, APP_NAME } from '../lib/branding'

interface Props {
  /** MUI sx passed through to the outer <img>. Sizing lives here. */
  sx?: SxProps<Theme>
  /**
   * `theme-aware` (default): follow palette mode.
   * `brand-panel`:           always render as white — for the dark
   *                          login hero and other permanent dark surfaces.
   */
  variant?: 'theme-aware' | 'brand-panel'
  /** Override alt text; defaults to `${APP_NAME} logo`. */
  alt?: string
}

export function BrandLogo({ sx, variant = 'theme-aware', alt }: Props) {
  // Track whether the dark variant loaded successfully so we can fall
  // back to the light file + filter if the customer hasn't uploaded a
  // dedicated dark image yet.
  const [darkAvailable, setDarkAvailable] = useState(true)

  return (
    <Box
      component="img"
      alt={alt ?? `${APP_NAME} logo`}
      src={(() => {
        if (variant === 'brand-panel') return APP_LOGO
        // theme-aware: attempt the dark file first; onError below
        // flips darkAvailable=false and we re-render with the light
        // file + filter.
        return darkAvailable ? APP_LOGO_DARK : APP_LOGO
      })()}
      onError={(e) => {
        // Only meaningful for the theme-aware dark attempt. Guard by
        // checking the src actually is the dark file so we don't
        // infinite-loop on a genuinely missing light logo.
        const img = e.currentTarget as HTMLImageElement
        if (img.src.endsWith(APP_LOGO_DARK)) setDarkAvailable(false)
      }}
      sx={[
        {
          objectFit: 'contain',
          display: 'inline-block',
          flexShrink: 0,
          // Filter rules:
          //   • brand-panel        → always white
          //   • theme-aware light  → native colours
          //   • theme-aware dark:
          //       – if dark file loaded   → native colours (already white/orange)
          //       – if dark file missing  → filter light file to pure white
          filter: (t) => {
            if (variant === 'brand-panel') return 'brightness(0) invert(1)'
            if (t.palette.mode !== 'dark') return 'none'
            return darkAvailable ? 'none' : 'brightness(0) invert(1)'
          },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    />
  )
}
